import Box from '@mui/material/Box';
import { QueryClientProvider } from 'react-query';
import { SnackbarProvider } from 'notistack';
import { DialogProvider } from './contexts/DialogContext';
import { LogProvider } from './contexts/LogContext';
import useAuth from './hooks/useAuth';
import useSettings from './hooks/useSettings';
import { EntityPreviewProvider } from './contexts/EntityPreviewContext';
import SettingsDrawer from './components/SettingsDrawer';
import SplashScreen from './components/SplashScreen';
import Log from './Log';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from './theme';
import Router from './routes';
import { queryClient } from './lib/queryClient';
import { useDeviceCatalog } from './lib/tether-microservices/routes/deviceCatalog';

// Defined in its own module so that `logout` — which lives above this provider — can clear
// it. See src/lib/queryClient.ts.

// Loads the device-type catalog once for the authenticated app so device icons + names resolve from the
// catalog everywhere (the hook mirrors it into the module cache read by getDeviceIcon / formatDeviceName).
const DeviceCatalogLoader = () => {
  useDeviceCatalog();
  return null;
};

function App() {
  const { settings } = useSettings();
  const auth = useAuth();

  const theme = createTheme({
    theme: settings.theme,
    roundedCorners: true,
  });
  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <DialogProvider>
          <SnackbarProvider dense maxSnack={3}>
            <LogProvider>
              <EntityPreviewProvider>
                <SettingsDrawer />
                <Box sx={{ display: 'flex' }}>
                  <Box
                    sx={{
                      width: settings.showLog ? `${100 - settings.logWidth}vw` : '100vw',
                      minHeight: '100vh',
                    }}
                  >
                    {auth.isInitialized ? (
                      <>
                        {auth.isAuthenticated && <DeviceCatalogLoader />}
                        <Router />
                      </>
                    ) : (
                      <SplashScreen />
                    )}
                  </Box>
                  {settings.showLog && <Log showDebug={settings.showDebug} />}
                </Box>
              </EntityPreviewProvider>
            </LogProvider>
          </SnackbarProvider>
        </DialogProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
